// AddUser
// --------
// Invite dialog for the Users page. Admin/Owner only by virtue of the page
// itself; the backend re-checks the role and the seat limit.
//
// Close behavior matches the other dialogs: a click outside never discards a
// half-typed invite, X, Cancel and Escape close immediately, and nothing
// closes while the invite is being sent.

import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Loader2, Mail, User, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import {
  ADMIN_DISPLAY_VALUE,
  ADMIN_GROUP,
  EMAIL_FORMAT_REGEX,
  INVALID_EMAIL_FORMAT_ERROR,
  PENTESTER_DISPLAY_VALUE,
  PENTESTER_GROUP,
} from "@/constants/definitions";
import { createNewUser } from "@/services/dataListOperations.service";
import { getGraphData } from "@/utils/transform";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import { doesUsernameExist } from "@/services/graphql/queries";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

const USER_ALREADY_EXISTS_ERROR = "A user with this email already exists";
const INVALID_GIVEN_NAME_ERROR = "First name is required";
const INVALID_FAMILY_NAME_ERROR = "Last name is required";
const UNABLE_TO_CREATE_USER_ERROR = "Unable to create user. Please try again.";

// A click outside is the one dismissal that is routinely accidental; every
// other path is deliberate and closes immediately.
type CloseRequestSource = "backdrop" | "dismiss";

interface AddUserProps {
  show: boolean;
  close: () => void;
}

export default function AddUser({ show, close }: AddUserProps) {
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(PENTESTER_GROUP);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setGivenName("");
      setFamilyName("");
      setEmail("");
      setRole(PENTESTER_GROUP);
      setErrorMessage("");
    }
  }, [show]);

  const hasUnsentInput = !!(givenName || familyName || email);

  const requestClose = useCallback(
    (source: CloseRequestSource) => {
      // Never close mid-send: the invite would still go out and the user would
      // not learn whether it did.
      if (isLoading) return;
      if (source === "backdrop" && hasUnsentInput) return;
      close();
    },
    [isLoading, hasUnsentInput, close]
  );

  const handleEscapeClose = useCallback(() => requestClose("dismiss"), [requestClose]);

  // Escape closes; focus moves into the dialog on open and back to the trigger
  // on close.
  useModalAccessibility(show, handleEscapeClose, dialogContainerRef);

  const handleInvite = useCallback(async () => {
    setErrorMessage("");

    if (!givenName.trim()) {
      setErrorMessage(INVALID_GIVEN_NAME_ERROR);
      return;
    }
    if (!familyName.trim()) {
      setErrorMessage(INVALID_FAMILY_NAME_ERROR);
      return;
    }
    if (!EMAIL_FORMAT_REGEX.test(email)) {
      setErrorMessage(INVALID_EMAIL_FORMAT_ERROR);
      return;
    }

    setIsLoading(true);
    try {
      // Check if user already exists
      const existsResponse = await API.graphql(
        graphqlOperation(doesUsernameExist, { username: email })
      );
      const userExists = getGraphData(existsResponse, "doesUsernameExist");
      if (userExists) {
        setErrorMessage(USER_ALREADY_EXISTS_ERROR);
        return;
      }

      // Create the user. On failure (e.g. the tenant hit its seat
      // limit between the button-enable check and this call), surface
      // the backend message verbatim, falling back to a generic one.
      const createResult = await createNewUser(givenName, familyName, email, role);
      if (createResult.success) {
        close();
      } else {
        setErrorMessage(createResult.message || UNABLE_TO_CREATE_USER_ERROR);
      }
    } catch (createError) {
      setErrorMessage(UNABLE_TO_CREATE_USER_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [givenName, familyName, email, role, close]);

  if (!show) return null;

  const isFormValid = givenName.trim() && familyName.trim() && email.trim();

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => requestClose("backdrop")}
        aria-hidden="true"
      />

      {/* Capped at 90vh with a scrolling body so the footer stays reachable on
          short viewports, matching the other dialogs. */}
      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-user-title"
        className="relative z-10 flex w-full max-w-md flex-col max-h-[90vh] rounded-xl bg-white shadow-xl border border-gray-200"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 id="add-user-title" className="text-lg font-semibold">Invite user</h2>
          <button
            type="button"
            onClick={() => requestClose("dismiss")}
            disabled={isLoading}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {errorMessage && (
            <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </div>
          )}

          {/* Name fields */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name" htmlFor="addUserGivenName">
              <Input
                id="addUserGivenName"
                type="text"
                value={givenName}
                onChange={(changeEvent) => { setGivenName(changeEvent.target.value); setErrorMessage(""); }}
                disabled={isLoading}
                placeholder="Jane"
                leftIcon={<User className="h-4 w-4" />}
              />
            </FormField>
            <FormField label="Last name" htmlFor="addUserFamilyName">
              <Input
                id="addUserFamilyName"
                type="text"
                value={familyName}
                onChange={(changeEvent) => { setFamilyName(changeEvent.target.value); setErrorMessage(""); }}
                disabled={isLoading}
                placeholder="Smith"
              />
            </FormField>
          </div>

          {/* Email */}
          <FormField label="Email" htmlFor="addUserEmail">
            <Input
              id="addUserEmail"
              type="email"
              value={email}
              onChange={(changeEvent) => { setEmail(changeEvent.target.value); setErrorMessage(""); }}
              disabled={isLoading}
              placeholder="jane@company.com"
              leftIcon={<Mail className="h-4 w-4" />}
            />
          </FormField>

          {/* Role selection */}
          <FormField label="Role">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRole(PENTESTER_GROUP)}
                disabled={isLoading}
                className={[
                  "flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                  role === PENTESTER_GROUP
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50",
                ].join(" ")}
              >
                {PENTESTER_DISPLAY_VALUE}
              </button>
              <button
                type="button"
                onClick={() => setRole(ADMIN_GROUP)}
                disabled={isLoading}
                className={[
                  "flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                  role === ADMIN_GROUP
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50",
                ].join(" ")}
              >
                {ADMIN_DISPLAY_VALUE}
              </button>
            </div>
          </FormField>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => requestClose("dismiss")} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleInvite}
            disabled={isLoading || !isFormValid}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            {isLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
            ) : (
              "Send invite"
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
